import { useId } from "react";

import { Dropdown, DropdownButton, EllipsisDropdownItem } from "@similarweb/ui-components";

import type { TranslationReplacements } from "app/filters/i18nFilter";
import type { Translatable } from "app/pages/sales-intelligence/types";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { DROPDOWN_BUTTON_TEXT_LENGTH_LIMIT } from "../../constants/common";
import { getSeparatedValuesPlusN } from "../../helpers/common";

import { StyledBaseDropdownButtonWrap, StyledDropdownContainer } from "./styles";

export type EmployeeProfileDropdownProps = {
    id: string;
    items: readonly string[];
    selectedIds?: readonly string[];
    emptyButtonText?: Translatable;
    width?: string;
    onAdd: (id: string) => void;
    onRemove: (id: string) => void;
    onToggle?: (isOpen: boolean) => void;
};

const EmployeeProfileDropdown = ({
    id,
    items,
    selectedIds = [],
    emptyButtonText,
    width = "366px",
    onAdd,
    onRemove,
    onToggle,
}: EmployeeProfileDropdownProps) => {
    const translate = useTranslation();
    const dropdownId = useId();

    const selectedIdsAsObject = selectedIds.reduce<Record<string, boolean>>((object, id) => {
        object[id] = true;

        return object;
    }, {});

    function getButtonText() {
        if (selectedIds.length === 0) {
            if (typeof emptyButtonText !== "undefined") {
                return translate(emptyButtonText.key, emptyButtonText.replacements as TranslationReplacements);
            }

            return translate(`si.contacts-search-profiles.${id}-dropdown.no-items`);
        }

        if (selectedIds.length === items.length) {
            return translate(`si.contacts-search-profiles.${id}-dropdown.all-items`);
        }

        return getSeparatedValuesPlusN(selectedIds, DROPDOWN_BUTTON_TEXT_LENGTH_LIMIT);
    }

    function handleClick(item: { id: string }) {
        if (selectedIds.includes(item.id)) {
            onRemove(item.id);
        } else {
            onAdd(item.id);
        }
    }

    return (
        <StyledDropdownContainer id={dropdownId}>
            <Dropdown
                width={width}
                dropdownPopupPlacement="ontop-left"
                closeOnItemClick={false}
                selectedIds={selectedIdsAsObject}
                dropdownPopupHeight={200}
                appendTo={`#${CSS.escape(dropdownId)}`}
                onClick={handleClick}
                onToggle={onToggle}
            >
                {[
                    <StyledBaseDropdownButtonWrap
                        key="employee-profiles-dropdown-button"
                        isEmpty={selectedIds.length === 0}
                    >
                        <DropdownButton>{getButtonText()}</DropdownButton>
                    </StyledBaseDropdownButtonWrap>,
                    ...items.map((item) => (
                        <EllipsisDropdownItem
                            key={item}
                            id={item}
                            showCheckBox
                        >
                            {item}
                        </EllipsisDropdownItem>
                    )),
                ]}
            </Dropdown>
        </StyledDropdownContainer>
    );
};

export default EmployeeProfileDropdown;
